import {MutableRefObject, useEffect, useState, useContext} from 'react';
import _ from "lodash";

import CheckBox from "../../../ui/CheckBox";
import GreenButton from '../../../ui/GreenButton';
import IfmsModal from '../../../ui/IfmsModal';
import InlineInputControlRow from "../../../ui/layout/InlineInputControlRow";
import Label from '../../../ui/Label';
import OrangeButton from '../../../ui/OrangeButton';
import SecondaryButton from "../../../ui/SecondaryButton";
import Select from '../../../ui/Select';
import SmartTable from '../../../uiControls/SmartTable';
import TextBox from "../../../ui/TextBox";
import useTableNavigation from '../../../ui/tables/useTableNavigation';
import { NumberedIndicatorRenderer, TextRenderer, DateRenderer, DateTimeRenderer, NullableCheckboxIconRenderer } from "../../../uiControls/SmartTableRenderers";

import useGlobalOkDialog from "../../../hooks/useGlobalOkDialog";
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import { useTypedFastStorage } from '../../../hooks/useFastStorage';
import Fieldset from '../../common/Fieldset';
import { ApplicableTableSettings } from '../../../tableSettings';
import ApplicationContext from "../../../ApplicationContext";

import { formatDate, nowDate, compareIso } from '../../../dateUtils';
import { FiscalYearEnd } from "../../../fms.core/FiscalYearEnd";
import { fiscalYearSettingsTable, yearEndHistoryTable } from './yearEndProcessing/tableSettings';
import ManageFiscalYearEnd from './ManageFiscalYearEnd';
import { FYHistory } from '../../../fms.core/FYHistory';
import { focusTabableByIndex } from '../../../util/inputUtils';

interface YearEndStorageKeys {
  modelStorage: string,
};

interface YearEndViewParams {
  id: string | undefined,
  onPopupClose?: () => void
};

interface YearEndLoadedDetailsViewParams {
  yearEnd: FiscalYearEnd,
  yearEndRef: MutableRefObject<FiscalYearEnd | null>,
  setYearEnd: (x: Partial<FiscalYearEnd>) => void,
  refreshCnt: number,
  setRefreshCnt: (x: number) => void,
  onPopupClose?: () => void
};

function getYearEndStorageKeys(): YearEndStorageKeys {
  return {modelStorage: 'report-year-end-processing'}
}

function Delay(delay: number) {
  return new Promise( res => setTimeout(res, delay) );
}
const monthDesc = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function YearEndImpl(params: YearEndViewParams) {
  const [refreshCnt, setRefreshCnt] = useState(0);
  const {modelStorage} = getYearEndStorageKeys();
  const [inStorage, setInStorage, removeYearEndFromStorage, yearEndProcessingRef] = useTypedFastStorage<FiscalYearEnd>(modelStorage, null);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const appContext = useContext(ApplicationContext);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[refreshCnt]);

  async function loadExisting() {
    const apiResult = await appContext.api.readYearEndData();
    if (!apiResult || !apiResult.success ) {
      await showGlobalOkDialog(<p>Failed to read Year End Data</p>);
      return;
    }

    const fiscalYearEnd: FiscalYearEnd = apiResult.result as FiscalYearEnd;

    if (fiscalYearEnd.fiscalMonth > 0 && fiscalYearEnd.fiscalMonth <= 12)
      fiscalYearEnd.fiscalMonthDesc = monthDesc[fiscalYearEnd.fiscalMonth - 1];
    
    fiscalYearEnd.fyPeriods.forEach((x, i) => 
      x.fyDesc = x.no
    );
    fiscalYearEnd.fyHistories.forEach((x, i) => 
      x.fyDesc = x.no
    );

    const closeDate = await appContext.api.getFieldValueFromInitData("F_CloseDate");
    const mpWord = await appContext.api.getFieldValueFromInitData("F_MPWORD");
    fiscalYearEnd.lastCloseDate = closeDate > mpWord ? closeDate : mpWord;

    removeYearEndFromStorage();
    setInStorage(fiscalYearEnd);
  }

  async function initialize() {
    loadExisting();
  }

  if (!inStorage) {   
    return null;
  }

  return (
    <YearEndInitializedImpl
      yearEnd={inStorage}
      yearEndRef={yearEndProcessingRef}
      setYearEnd={setInStorage}
      refreshCnt={refreshCnt}
      setRefreshCnt={setRefreshCnt}
      onPopupClose={params.onPopupClose}
    />
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function YearEndInitializedImpl({ yearEnd, yearEndRef, setYearEnd, refreshCnt, setRefreshCnt, onPopupClose }: YearEndLoadedDetailsViewParams) {
  const [selectedRow, setSelectedRow] = useState(-1);
  const [checkReverse, setCheckReverse] = useState(false);
  const [doingYearEnd, setDoingYearEnd] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const [showManageFyEnd, setShowManageFyEnd] = useState(false);
  const [lastHistoryId, setLastHistoryId] = useState(0);

  const { selectTableRow, clearTableSelection } = useTableNavigation('fiscalYearSettingsTable', onChangeSelectRow, onChangeSelectRow, [], true, true);
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
 
  useEffect(() => {
    setLastHistoryId(yearEnd.fyHistories.length > 0 ? yearEnd.fyHistories[0].id : 0);
    setTimeout(() => { checkDefault(); }, 10);
    focusTabableByIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRow > (yearEnd.fyPeriods.length - 1)){
      clearTableSelection();
      onChangeSelectRow(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearEnd]);

  function checkDefault() {
    if (selectedRow === -1) {
      let defaultIndex = yearEnd.fyPeriods.findIndex(x => compareIso(x.beginDate ?? '', nowDate()) < 0 && compareIso(nowDate(), x.endDate ?? '') < 0);
      if (defaultIndex >= 0) {
        if (defaultIndex + 1 < yearEnd.fyPeriods.length) {
          defaultIndex++;
        }
      }
      else if(defaultIndex === -1)
        defaultIndex = 0;

      onChangeSelectRow(defaultIndex);

      setLastHistoryId(yearEnd.fyHistories.length > 0 ? yearEnd.fyHistories[0].id : 0);
    }
  }

  function onChangeSelectRow(row:any) {
    setResultMsg("");
    showProgress(false);
    setDoingYearEnd(false);
    selectTableRow(row);
    setSelectedRow(row);
  }

  function onChange(change: Partial<FiscalYearEnd>) {
    setYearEnd(change);
  }

  function showProgress(show: boolean) {
    const el = document.getElementById('yearEnd-progress');
    if (el) {
      el.style.display = show ? 'block' : 'none';
    }
  }

  function setProgressStatus(percentage: number) {
    const progress = document.getElementById('yearEnd-progress');
    if (progress) {
      progress.style.width = `${percentage}%`;
    }
  }

  function setFYStatus(index: number, status: boolean) {
    const _list = _.cloneDeep(yearEnd.fyPeriods);
    _list[index].yearEnd = status;
    onChange({fyPeriods: _list});
  }

  async function refreshHistory() {
    const apiResult = await appContext.api.readFYHistory();
    if (!apiResult || !apiResult.success ) {
      await showGlobalOkDialog(<p>Failed to read history : {apiResult.message}</p>);
      return;
    }

    const histories: FYHistory[] = apiResult.result;
    histories.forEach((x) => 
      x.fyDesc = x.no
    );

    onChange({ fyHistories: histories });
  }

  async function runYearEndProc() {
    if (selectedRow < 0) return;

    setDoingYearEnd(true);

    // 1. Fiscal Year List에서 필요시 Next Year정보를 추가
    let result:number = 0;
    let closeDate = yearEnd.fyPeriods[selectedRow].endDate;

    try {
      result = 5;

      let beginDate = yearEnd.fyPeriods[selectedRow].beginDate;
      let endDate = closeDate;
      let isFinalYear = (selectedRow === 0);

      if (isFinalYear) {
        const apiResult = await appContext.api.checkFYPeriod(beginDate, endDate);
        if (!apiResult || !apiResult.success ) {
          await showGlobalOkDialog(<p>Failed to check Fiscal Year Period</p>);
          return;
        }
      }

      // 2. 시작 위치 설정

      let beginRow = 0;
      for (let k = selectedRow; k < yearEnd.fyPeriods.length; k++) {
        if (yearEnd.fyPeriods[k].yearEnd) break;
        else beginRow = k;
      }

      // 3. 데이터 검증. Recog 처리 안된 것이 있는 경우 Warning. 

      const apiResult2 = await appContext.api.checkRecogStatus(endDate);
      if (!apiResult2 || !apiResult2.success ) {
        await showGlobalOkDialog(<p>Failed to check Recog Status</p>);
        return;
      }

      if (!apiResult2.result) {
        const confirm = await showGlobalYesNoDialog(
          <div>
            <p className='multiline'>There are unrecognized Sales/Cost for year end processing.<br />
            Unrecognized Sales/Cost will affect the income Statement.<br />
            It is recommended to recognize Sales/Cost before year end processing.<br />
            Do you want to proceed with unrecognized Sales/Cost ?</p>
          </div>
        ,false, 0, true);

        if (!confirm) {
          setDoingYearEnd(false);
          return;
        }
      }

      showProgress(true);
      setProgressStatus(0);

      // 4. 실제 Year End Processing 수행
      let prog: number;

      for (let k = beginRow; k >= selectedRow; k--) {
        prog = Math.floor((beginRow - k) / (beginRow - selectedRow + 1) * 90);
        setProgressStatus(prog);

        beginDate = yearEnd.fyPeriods[k].beginDate;
        endDate = yearEnd.fyPeriods[k].endDate;

        const apiResult3 = await appContext.api.runYearEnd(beginDate, endDate, yearEnd.earningGLNo);
        if (!apiResult3 || !apiResult3.success ) {
          await showGlobalOkDialog(<p>Failed to run Year End Processing</p>);
          return;
        }
        result = apiResult3.result;
        setFYStatus(k, true);
          
        prog = Math.floor((beginRow - k + 1) / (beginRow - selectedRow + 1) * 90);
        setProgressStatus(prog);

        // Save & Reload History
        const apiResult4 = await appContext.api.saveFiscalYearHistory('Year End', yearEnd.fyPeriods[k].fyDesc, yearEnd.fyPeriods[k].fiscalYear);
        if (!apiResult4 || !apiResult4.success ) {
          await showGlobalOkDialog(<p>Failed to save history : {apiResult4.message}</p>);
          return;
        }
        refreshHistory();

        if (result> 0) {
          setProgressStatus(100);
          throw new Error();
        }
      }

      if (result <= 0) {
        const apiResult5 = await appContext.api.periodClose(closeDate);
        if (!apiResult5 || !apiResult5.success ) {
          await showGlobalOkDialog(<p>Failed to period close : {apiResult5.message}</p>);
          setDoingYearEnd(false);
          return;
        }
        result = apiResult5.reuslt;
        if (result > 0) {
          setProgressStatus(100);
          throw new Error();
        }
      }

      setProgressStatus(100);
      setResultMsg("++ Year End Processing is completed successfully.");
    } catch (e:any) {
      switch (result) {
        case 7:
          setResultMsg("++ Year End Processing is completed.\r\n     but Close Operation has failed.");
          break;
        case 5:
          setResultMsg("++ System has some problem.");
          break;
        case 2:
          setResultMsg("++ Year End Processing has failed. Please check accounting settings.");
          break;
        case 1:
          setResultMsg("++ Year End Processing is completed.\r\n     but you have an error somewhere in accounting process.");
          break;
        default:
          setResultMsg("++ Year End Processing is completed successfully.");
          break;
      }
    }

    setProgressStatus(100);
  }

  async function runReverseProc() {
    if (selectedRow < 0) return;

    setDoingYearEnd(true);

    try {
      // 1. 시작 위치 설정

      let beginRow = 0;
      for (let k = selectedRow; k >= 0; k--) {
        if (yearEnd.fyPeriods[k].yearEnd) beginRow = k;
        else break;
      }

      showProgress(true);
      setProgressStatus(0);

      // 2. 실제 Reverse Processing 수행
      let prog: number;
      let thisFiscalYear: number;
      let nextFiscalYear: number;

      for (let k = beginRow; k <= selectedRow; k++) {
        prog = Math.floor((k - beginRow) / (selectedRow - beginRow + 1) * 100);
        setProgressStatus(prog);

        thisFiscalYear = yearEnd.fyPeriods[k].fiscalYear;
        nextFiscalYear = yearEnd.fyPeriods[k - 1].fiscalYear;

        const apiResult = await appContext.api.runReverse(thisFiscalYear, nextFiscalYear);
        if (!apiResult || !apiResult.success ) {
          await showGlobalOkDialog(<p>Failed to run Reverse Processing : {apiResult.message}</p>);
          setDoingYearEnd(false);
          return;
        }

        setFYStatus(k, false);

        prog = Math.floor((k - beginRow + 1) / (selectedRow - beginRow + 1) * 100);
        setProgressStatus(prog);
        Delay(500);

        // Save & Reload History
        const apiResult2 = await appContext.api.saveFiscalYearHistory('Reverse', yearEnd.fyPeriods[k].fyDesc, yearEnd.fyPeriods[k].fiscalYear);
        if (!apiResult2 || !apiResult2.success ) {
          await showGlobalOkDialog(<p>Failed to save history : {apiResult2.message}</p>);
          setDoingYearEnd(false);
          return;
        }
        refreshHistory();
        
      }

    } catch (e:any) {
    }

    Delay(500);
    showProgress(false);
    setCheckReverse(false);
    setDoingYearEnd(false);
  }

  const yearEndListSettings: ApplicableTableSettings = {
    tableName: 'fiscalYearSettingsTable',
    customTableId: 'fiscalYearSettingsTable',
    ...fiscalYearSettingsTable,
    data: yearEnd.fyPeriods,
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'fyDesc', Cell: TextRenderer },
      { accessor: 'beginDate', Cell: DateRenderer },
      { accessor: 'endDate', Cell: DateRenderer },
      { accessor: 'yearEnd', Cell: NullableCheckboxIconRenderer },
    ],
    footerRow: [ 
    ],
    height: '220px',
  };

  const yearEndHistorySettings: ApplicableTableSettings = {
    tableName: 'yearEndHistoryTable',
    customTableId: 'yearEndHistoryTable',
    ...yearEndHistoryTable,
    data: yearEnd.fyHistories,
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'date', Cell: DateTimeRenderer },
      { accessor: 'fyDesc', Cell: TextRenderer },
      { accessor: 'action', Cell: TextRenderer },
      { accessor: 'user', Cell: TextRenderer },
    ],
    footerRow: [ 
    ],
    height: '120px',
    rowProps: (row: any) => {
      if (yearEnd.fyHistories[row.index].id > lastHistoryId) {
          return {
            style: { backgroundColor: 'rgb(160, 225, 160)' }
          };
      }

      return undefined;
    },    
  };

  return (
    <div className='report-popup'>
      <Fieldset label="Fiscal Year Settings">
        <div style={{display: 'flex', marginTop: '5px'}}>
          <div style={{width: '200px', marginLeft: '30px'}}>
            <InlineInputControlRow label="Fiscal End Month" smallMargin mode="smaller">
              <TextBox name='year-end-fiscalMonthDesc' disabled value={yearEnd.fiscalMonthDesc} style={{textAlign: 'center'}} />
            </InlineInputControlRow>

            <InlineInputControlRow label="Start Date" smallMargin mode="smaller">
              <TextBox name='year-end-acctStartDate' disabled value={formatDate(yearEnd.acctStartDate, "YYYY.MM")} style={{textAlign: 'center'}} />
            </InlineInputControlRow>
            
            <InlineInputControlRow label="Last Period Close" smallMargin mode="smaller">
              <TextBox name='year-end-acctLastPCDate' disabled value={formatDate(yearEnd.lastCloseDate)} style={{textAlign: 'center'}} />
            </InlineInputControlRow>
          </div>

          <div style={{width: '200px', paddingLeft: '20px', paddingRight: '20px', alignSelf: 'center'}}>
            <SecondaryButton name='year-end-manage-button' focusable={true} size="regular" style={{width: '100%'}} onClick={() => setShowManageFyEnd(true)}>Manage Fiscal Year End</SecondaryButton>
          </div>

        </div>
      </Fieldset>

      <div style={{marginLeft: '30px', marginTop: '20px', }}>
        <Label label="Fiscal Year End Processing" bold />
      </div>

      <div>
        <SmartTable {...yearEndListSettings} />
      </div>

      <div style={{display: 'flex', height: '20px', marginTop: '20px', marginLeft: '30px', marginRight: '20px', marginBottom: '18px'}}>
        <div style={{width: '100px'}}>
          <Label label="Fiscal Year Ending" bold />
        </div>
        <div style={{width: '100px', paddingLeft: '20px'}}>
          <Select
            name='year-end-fyPeriod' 
            value={selectedRow}
            items={yearEnd.fyPeriods.map((x, i) => ({ label: x.fyDesc, value: i }))}
            onChange={e => onChangeSelectRow(e.target.value)}
            />
        </div>
        <div style={{width: '180px', paddingLeft: '20px'}}>
          <GreenButton name='year-end-processing-button' focusable={true} disabled={(selectedRow > -1 && (yearEnd.fyPeriods[selectedRow].yearEnd || compareIso(yearEnd.fyPeriods[selectedRow].beginDate??'', nowDate()) > 0)) || doingYearEnd} size="small" 
            onClick={runYearEndProc} 
            >Fiscal Year End Processing 
          </GreenButton>
        </div>
      </div>

      {selectedRow > -1 && (!yearEnd.fyPeriods[selectedRow].yearEnd || doingYearEnd) && 

      <div style={{ height: '35px'}}>
        <div style={{marginLeft: '30px'}}>
          <p className='multiline' id='year-end-resultMsg' style={{color: 'red', fontWeight: 'bold'}}>{resultMsg}</p>
        </div>
      </div>

      }

      {selectedRow > -1 && yearEnd.fyPeriods[selectedRow].yearEnd && !doingYearEnd && 

      <div style={{display: 'flex', height: '35px', marginLeft: '30px', marginRight: '20px'}}>
        <div>
          <CheckBox label='Please check here to reverse Year End Processing' id='year-end-checkReverse-chk' focusId='year-end-checkReverse' value={checkReverse} onChange={checked => setCheckReverse(checked)} />
        </div>
        <div style={{width: '180px', paddingLeft: '20px'}}>
          <OrangeButton name='year-end-reverse-button' focusable={true} disabled={!checkReverse} onClick={runReverseProc} mode="small" style={{height: '20px', fontSize: '11px', paddingTop: '3px', paddingBottom: '2px'}}>Reverse</OrangeButton>
        </div>
      </div>

      }

      <div style={{height: '10px'}}>
        <div id='yearEnd-progress' className='report-hr-nomargin' style={{display: 'none', width: '100%'}}>&nbsp;</div>
      </div>

      <div style={{marginLeft: '30px', marginTop: '20px', }}>
        <Label label="Year End History" bold />
      </div>

      <div>
        <SmartTable {...yearEndHistorySettings} />
      </div>

      <div className='report-footer'>
        <SecondaryButton 
          name='year-end-cancel-button'
          focusable={true} 
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          Close
        </SecondaryButton>
      </div>
      {!!showManageFyEnd ? (
        <IfmsModal isBlueHeader cancelOnEsc onCancel={() => setShowManageFyEnd(false)} draggable isOpen={showManageFyEnd} title="Manage Fiscal Year End" width="600px">
          <ManageFiscalYearEnd yearEnd={yearEnd} refreshCnt={refreshCnt} setRefreshCnt={setRefreshCnt} onPopupClose={() => setShowManageFyEnd(false)} />
        </IfmsModal>
      ) : null}
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const YearEndProcessingDetails = ({onPopupClose}: {onPopupClose?: () => void}) => {
  return (
    <YearEndImpl id={''} onPopupClose={onPopupClose} key={`yearEndProcessing-details`}/>
  );
};
export default YearEndProcessingDetails;
